import React, { useEffect, useRef, useState } from 'react';
import { api, ApiError, mutate } from '../shared/api';
import { formatMoney, formatNumber, formatDate } from '../shared/formatting';

type Locale = 'hr' | 'en';
type Nacin = 'rucno' | 'recenica' | 'automatika';
type Status = 'ceka' | 'radi' | 'gotovo' | 'greska';

type Parametri = {
    konta?: string[];
    plan_izvor?: string | null;
    objasnjenje?: string | null;
    po_partneru?: boolean;
    po_dokumentu?: boolean;
    tolerancija?: number;
    max_kombinacija?: number;
    do?: string | null;
};

type Uparivanje = {
    id: number; godina: number; nacin: Nacin; uputa: string | null; status: Status;
    napredak: number | null; greska: string | null; created_at: string; finished_at: string | null;
    parametri: Parametri | null;
};

type ListResp = { uparivanja: Uparivanje[]; claude: boolean };

type Pravila = { dokument: boolean; iznos: boolean; kombinacija: number; saldo_partnera: boolean };
type PartnerRow = { partner: string | null; partner_sifra: string | null; n: number; saldo: number };
type NeuparenaStavka = {
    id: number; iznos: number; partner: string | null; partner_sifra: string | null; dokument: string | null;
    datum: string | null; razdoblje: string | null; opis: string | null; tem: string | null;
};
type KontoRez = {
    konto: string; naziv: string | null; stavki: number; uparenih: number; neuparenih: number;
    saldo: number; saldo_neuparenih: number; pravila: Pravila;
    partneri: PartnerRow[]; neuparene: NeuparenaStavka[]; neuparene_skraceno: boolean;
};
type Rezultat = {
    parametri: Parametri;
    ukupno: { stavki: number; uparenih: number; neuparenih: number; saldo_neuparenih: number };
    konta: KontoRez[];
};
type Detail = Uparivanje & { rezultat: Rezultat | null };

const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };

/**
 * 10.1 Uparivanje otvorenih stavki. Tri nacina pokretanja (rucno po kontima,
 * uputa recenicom koju tumaci Claude, ili automatika kroz sva konta
 * razreda 1 i 2); posao se vrti u pozadini (status ceka/radi/gotovo/greska),
 * frontend samo pollia dok ne zavrsi. Rezultat je po kontu s prijedlogom
 * pravila uparivanja i ostatkom neuparenih stavki, iz kojih se moze napraviti
 * nacrt knjizenja zatvaranja (protukonto + datum) koji ide u Temeljnice i nacrte.
 */
export function GlUparivanjeTab({ locale, token, company, godina, onOpenNacrti }: {
    locale: Locale; token: string | null; company: string; godina: number; onOpenNacrti?: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const [nacin, setNacin] = useState<Nacin>('rucno');
    const [konta, setKonta] = useState('');
    const [uputa, setUputa] = useState('');
    const [poPartneru, setPoPartneru] = useState(true);
    const [poDokumentu, setPoDokumentu] = useState(true);
    const [tolerancija, setTolerancija] = useState('0');
    const [maxKombinacija, setMaxKombinacija] = useState(3);
    const [doDatuma, setDoDatuma] = useState('');

    const [lista, setLista] = useState<Uparivanje[]>([]);
    const [claude, setClaude] = useState(false);
    const [selId, setSelId] = useState<number | null>(null);
    const [detail, setDetail] = useState<Detail | null>(null);
    const [pokrecem, setPokrecem] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [selKonto, setSelKonto] = useState<string | null>(null);
    const [odabrane, setOdabrane] = useState<Set<number>>(new Set());
    const [protukonto, setProtukonto] = useState('');
    const [nacrtDatum, setNacrtDatum] = useState(() => new Date().toISOString().slice(0, 10));
    const [nacrtOpis, setNacrtOpis] = useState('');
    const [nacrtBusy, setNacrtBusy] = useState(false);
    const [nacrtGotov, setNacrtGotov] = useState<number | null>(null);

    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadList = () => api<ListResp>(`/gl-analitika/uparivanja?company=${company}`, token)
        .then((r) => { setLista(r.uparivanja); setClaude(r.claude); })
        .catch((e) => setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')));

    useEffect(() => { setSelId(null); setDetail(null); loadList(); }, [company, token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (timer.current) { clearTimeout(timer.current); timer.current = null; }
        if (selId === null) { setDetail(null); return; }
        let cancelled = false;
        const load = () => {
            api<Detail>(`/gl-analitika/uparivanja/${selId}`, token)
                .then((d) => {
                    if (cancelled) return;
                    setDetail(d);
                    if (d.status === 'ceka' || d.status === 'radi') {
                        timer.current = setTimeout(load, 3000);
                    } else {
                        loadList();
                    }
                })
                .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : t('Učitavanje nije uspjelo.', 'Loading failed.')); });
        };
        load();
        return () => { cancelled = true; if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selId, token]);

    useEffect(() => {
        setSelKonto(null);
        setOdabrane(new Set());
        setNacrtGotov(null);
    }, [selId]);

    async function pokreni() {
        setError(null);
        setPokrecem(true);
        try {
            const parametri = {
                po_partneru: poPartneru,
                po_dokumentu: poDokumentu,
                tolerancija: Number(tolerancija.replace(',', '.')) || 0,
                max_kombinacija: maxKombinacija,
            };
            const body: Record<string, unknown> = { nacin, godina, parametri };
            if (doDatuma) body.do = doDatuma;
            if (nacin === 'rucno') {
                const list = konta.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
                body.konta = list;
            } else if (nacin === 'recenica') {
                body.uputa = uputa;
            }
            const r = await mutate<{ id: number }>(`/gl-analitika/uparivanja?company=${company}`, token, 'POST', body);
            setSelId(r.id);
            await loadList();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Pokretanje nije uspjelo.', 'Could not start.'));
        } finally {
            setPokrecem(false);
        }
    }

    function toggleOdabrano(id: number) {
        setOdabrane((cur) => {
            const next = new Set(cur);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }

    async function napraviNacrt() {
        if (!selId || !protukonto.trim() || odabrane.size === 0) return;
        setError(null);
        setNacrtBusy(true);
        try {
            const r = await mutate<{ id: number }>(`/gl-analitika/uparivanja/${selId}/nacrt`, token, 'POST', {
                ids: Array.from(odabrane),
                protukonto: protukonto.trim(),
                datum: nacrtDatum,
                opis: nacrtOpis || undefined,
            });
            setNacrtGotov(r.id);
            setOdabrane(new Set());
        } catch (e) {
            setError(e instanceof ApiError ? e.message : t('Nacrt nije napravljen.', 'Draft creation failed.'));
        } finally {
            setNacrtBusy(false);
        }
    }

    const m = (v: number) => formatMoney(v, locale);
    const statusLabel = (s: Status) => (s === 'ceka' ? t('čeka', 'queued') : s === 'radi' ? t('radi', 'running')
        : s === 'gotovo' ? t('gotovo', 'done') : t('greška', 'error'));

    const konteRez = detail?.rezultat?.konta.find((k) => k.konto === selKonto) ?? null;

    return (
        <div>
            <p className="muted" style={{ marginTop: 0 }}>
                {t('Traži parove u otvorenim stavkama (duguje/potražuje koji se poništavaju) po partneru, broju dokumenta i/ili iznosu s tolerancijom. Ostatak ide u prijedlog nacrta zatvaranja.',
                    'Looks for matching pairs in open items (debit/credit that cancel out) by partner, document number and/or amount with a tolerance. The remainder goes into a closing draft proposal.')}
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <label style={{ border: '1px solid #ccc', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', background: nacin === 'rucno' ? '#f0f4ff' : undefined }}>
                    <input type="radio" name="nacin" checked={nacin === 'rucno'} onChange={() => setNacin('rucno')} />{' '}
                    <strong>{t('Ručno - odaberi konta', 'Manual - pick accounts')}</strong>
                </label>
                <label style={{ border: '1px solid #ccc', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', background: nacin === 'recenica' ? '#f0f4ff' : undefined }}>
                    <input type="radio" name="nacin" checked={nacin === 'recenica'} onChange={() => setNacin('recenica')} />{' '}
                    <strong>{t('Uputa rečenicom', 'Instruction in a sentence')}</strong>
                </label>
                <label style={{ border: '1px solid #ccc', borderRadius: 6, padding: '8px 10px', cursor: 'pointer', background: nacin === 'automatika' ? '#f0f4ff' : undefined }}>
                    <input type="radio" name="nacin" checked={nacin === 'automatika'} onChange={() => setNacin('automatika')} />{' '}
                    <strong>{t('Automatika - sva konta razreda 1 i 2', 'Automatic - all class 1 and 2 accounts')}</strong>
                </label>
            </div>

            {nacin === 'rucno' ? (
                <div style={{ marginBottom: 10 }}>
                    <label>{t('Konta (prefiksi, odvojeni zarezom ili razmakom)', 'Accounts (prefixes, comma or space separated)')}<br />
                        <input value={konta} onChange={(e) => setKonta(e.target.value)} placeholder="1200, 2200" style={{ width: 320 }} />
                    </label>
                </div>
            ) : null}

            {nacin === 'recenica' ? (
                <div style={{ marginBottom: 10 }}>
                    <label>{t('Uputa', 'Instruction')}<br />
                        <textarea value={uputa} onChange={(e) => setUputa(e.target.value)} rows={3} style={{ width: '100%', maxWidth: 640 }}
                            placeholder={t('npr. Upari kupce na 1200 po partneru i broju računa, tolerancija 1 euro', 'e.g. Match customers on account 1200 by partner and invoice number, 1 euro tolerance')} />
                    </label>
                    <p className="muted" style={{ marginTop: 4 }}>
                        {claude
                            ? t('Konta iz upute odabire Claude, zatim BIS upari.', 'Claude picks the accounts from the instruction, then BIS matches.')
                            : t('Claude API nije postavljen: konta se uzimaju iz brojeva u uputi.', 'Claude API is not configured: accounts are taken from the numbers in the instruction.')}
                    </p>
                </div>
            ) : null}

            {nacin === 'automatika' ? (
                <p style={{ color: '#9a3412', marginBottom: 10 }}>
                    {t('Najsporije: prolazi kroz sva konta otvorenih stavki. Može trajati više minuta.',
                        'Slowest option: goes through all open-item accounts. Can take several minutes.')}
                </p>
            ) : null}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
                <label title={t('Stavke istog partnera se paruju prije ostalih', 'Items of the same partner are matched first')}>
                    <input type="checkbox" checked={poPartneru} onChange={(e) => setPoPartneru(e.target.checked)} /> {t('po partneru', 'by partner')}
                </label>
                <label title={t('Stavke istog broja dokumenta se paruju prije ostalih', 'Items with the same document number are matched first')}>
                    <input type="checkbox" checked={poDokumentu} onChange={(e) => setPoDokumentu(e.target.checked)} /> {t('po dokumentu', 'by document')}
                </label>
                <label>{t('Tolerancija (EUR)', 'Tolerance (EUR)')}<br />
                    <input value={tolerancija} onChange={(e) => setTolerancija(e.target.value)} style={{ width: 90 }} />
                </label>
                <label>{t('Max kombinacija', 'Max combination')}<br />
                    <select value={maxKombinacija} onChange={(e) => setMaxKombinacija(Number(e.target.value))}>
                        <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                    </select>
                </label>
                <label>{t('Do datuma', 'Up to date')}<br />
                    <input type="date" value={doDatuma} onChange={(e) => setDoDatuma(e.target.value)} />
                </label>
                <button type="button" className="compact" onClick={pokreni}
                    disabled={pokrecem || (nacin === 'rucno' && !konta.trim()) || (nacin === 'recenica' && !uputa.trim())}>
                    {pokrecem ? t('Pokrećem…', 'Starting…') : t('Pokreni', 'Run')}
                </button>
            </div>

            {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

            <div style={{ overflow: 'auto', maxHeight: 240, margin: '0.8rem 0' }}>
                <table className="data-table compact">
                    <thead><tr>
                        <th>ID</th><th>{t('Način', 'Mode')}</th><th>{t('Uputa / konta', 'Instruction / accounts')}</th>
                        <th>{t('Status', 'Status')}</th><th>{t('Vrijeme', 'Time')}</th>
                    </tr></thead>
                    <tbody>
                        {lista.map((u) => {
                            const opis = u.nacin === 'rucno' ? (u.parametri?.konta ?? []).join(', ')
                                : u.nacin === 'automatika' ? t('sva konta 1 i 2', 'all accounts 1 and 2')
                                    : (u.uputa ?? '');
                            const skraceno = opis.length > 70 ? `${opis.slice(0, 70)}…` : opis;
                            return (
                                <tr key={u.id} onClick={() => setSelId(u.id)} style={{ cursor: 'pointer', fontWeight: u.id === selId ? 700 : 400 }}>
                                    <td>{u.id}</td><td>{u.nacin}</td><td title={opis}>{skraceno}</td>
                                    <td>{statusLabel(u.status)}{u.status === 'radi' && u.napredak !== null ? ` ${u.napredak}%` : ''}</td>
                                    <td>{formatDate(u.created_at, locale)}</td>
                                </tr>
                            );
                        })}
                        {lista.length === 0 ? <tr><td colSpan={5} className="muted">{t('Još nema pokrenutih uparivanja.', 'No matching runs yet.')}</td></tr> : null}
                    </tbody>
                </table>
            </div>

            {detail ? (
                <div style={{ marginTop: 10 }}>
                    {detail.parametri?.plan_izvor ? (
                        <p className="muted">{t('Izvor plana', 'Plan source')}: {detail.parametri.plan_izvor}</p>
                    ) : null}
                    {detail.parametri?.objasnjenje ? (
                        <p className="muted">{detail.parametri.objasnjenje}</p>
                    ) : null}

                    {detail.status === 'ceka' || detail.status === 'radi' ? (
                        <div style={{ margin: '0.6rem 0' }}>
                            <div style={{ background: '#e5e7eb', borderRadius: 4, height: 10, width: 320, overflow: 'hidden' }}>
                                <div style={{ background: '#6b7280', height: '100%', width: `${detail.napredak ?? 0}%` }} />
                            </div>
                            <p className="muted" style={{ margin: '4px 0' }}>
                                {statusLabel(detail.status)}{detail.napredak !== null ? ` - ${detail.napredak}%` : ''}
                            </p>
                        </div>
                    ) : null}

                    {detail.status === 'greska' && detail.greska ? <p style={{ color: '#b91c1c' }}>{detail.greska}</p> : null}

                    {detail.status === 'gotovo' && detail.rezultat ? (
                        <>
                            <p>
                                <strong>{formatNumber(detail.rezultat.ukupno.stavki, locale)}</strong> {t('stavki', 'items')}
                                {' · '}{t('upareno', 'matched')} <strong>{formatNumber(detail.rezultat.ukupno.uparenih, locale)}</strong>
                                {' · '}{t('neupareno', 'unmatched')} <strong>{formatNumber(detail.rezultat.ukupno.neuparenih, locale)}</strong>
                                {' · '}{t('saldo neuparenih', 'unmatched balance')} <strong>{m(detail.rezultat.ukupno.saldo_neuparenih)}</strong>
                            </p>
                            <div style={{ overflow: 'auto' }}>
                                <table className="data-table compact">
                                    <thead><tr>
                                        <th>{t('Konto', 'Account')}</th><th>{t('Naziv', 'Name')}</th><th style={num}>{t('Stavki', 'Items')}</th>
                                        <th style={num}>{t('Uparenih', 'Matched')}</th><th style={num}>{t('Neuparenih', 'Unmatched')}</th>
                                        <th style={num}>{t('Saldo', 'Balance')}</th><th style={num}>{t('Saldo neuparenih', 'Unmatched balance')}</th>
                                        <th>{t('Pravila', 'Rules')}</th>
                                    </tr></thead>
                                    <tbody>
                                        {detail.rezultat.konta.map((k) => (
                                            <tr key={k.konto} onClick={() => setSelKonto(k.konto)} style={{ cursor: 'pointer', fontWeight: k.konto === selKonto ? 700 : 400 }}>
                                                <td>{k.konto}</td><td>{k.naziv ?? ''}</td><td style={num}>{formatNumber(k.stavki, locale)}</td>
                                                <td style={num}>{formatNumber(k.uparenih, locale)}</td><td style={num}>{formatNumber(k.neuparenih, locale)}</td>
                                                <td style={num}>{m(k.saldo)}</td><td style={num}>{m(k.saldo_neuparenih)}</td>
                                                <td className="muted">
                                                    {t('dok', 'doc')} {k.pravila.dokument ? '✓' : '✗'} · {t('iznos', 'amt')} {k.pravila.iznos ? '✓' : '✗'}
                                                    {' · '}{t('komb', 'comb')} {k.pravila.kombinacija} · {t('saldo', 'bal')} {k.pravila.saldo_partnera ? '✓' : '✗'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {konteRez ? (
                                <div style={{ marginTop: 14 }}>
                                    <h3 style={{ margin: '0.6rem 0' }}>{konteRez.konto} - {konteRez.naziv ?? ''}</h3>

                                    <h4 style={{ margin: '0.4rem 0' }}>{t('Saldo po partneru', 'Balance per partner')} ({t('top 50', 'top 50')})</h4>
                                    <div style={{ overflow: 'auto', maxHeight: 220, marginBottom: 10 }}>
                                        <table className="data-table compact">
                                            <thead><tr><th>{t('Partner', 'Partner')}</th><th>{t('Šifra', 'Code')}</th><th style={num}>{t('Broj stavki', 'Items')}</th><th style={num}>{t('Saldo', 'Balance')}</th></tr></thead>
                                            <tbody>
                                                {konteRez.partneri.slice(0, 50).map((p, i) => (
                                                    <tr key={`${p.partner_sifra ?? ''}-${i}`}>
                                                        <td>{p.partner ?? ''}</td><td>{p.partner_sifra ?? ''}</td>
                                                        <td style={num}>{formatNumber(p.n, locale)}</td><td style={num}>{m(p.saldo)}</td>
                                                    </tr>
                                                ))}
                                                {konteRez.partneri.length === 0 ? <tr><td colSpan={4} className="muted">{t('Nema neuparenog salda po partneru.', 'No unmatched balance per partner.')}</td></tr> : null}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '0.4rem 0' }}>
                                        <h4 style={{ margin: 0 }}>{t('Neuparene stavke', 'Unmatched items')}</h4>
                                        <button type="button" className="secondary compact" onClick={() => setOdabrane(new Set(konteRez.neuparene.map((x) => x.id)))}>
                                            {t('označi sve', 'select all')}
                                        </button>
                                        <button type="button" className="secondary compact" onClick={() => setOdabrane(new Set())}>
                                            {t('poništi', 'clear')}
                                        </button>
                                    </div>
                                    {konteRez.neuparene_skraceno ? (
                                        <p className="muted">{t('Prikazane su najveće stavke; za sve pokreni ručno samo to konto.', 'The largest items are shown; to see all, run matching manually for just this account.')}</p>
                                    ) : null}
                                    <div style={{ overflow: 'auto', maxHeight: 300, marginBottom: 10 }}>
                                        <table className="data-table compact">
                                            <thead><tr>
                                                <th></th><th>{t('Datum', 'Date')}</th><th>{t('Razdoblje', 'Period')}</th><th>{t('Temeljnica', 'Journal')}</th>
                                                <th>{t('Dokument', 'Document')}</th><th>{t('Partner', 'Partner')}</th><th>{t('Opis', 'Description')}</th><th style={num}>{t('Iznos', 'Amount')}</th>
                                            </tr></thead>
                                            <tbody>
                                                {konteRez.neuparene.map((s) => (
                                                    <tr key={s.id}>
                                                        <td><input type="checkbox" checked={odabrane.has(s.id)} onChange={() => toggleOdabrano(s.id)} /></td>
                                                        <td>{s.datum ? formatDate(s.datum, locale) : ''}</td><td>{s.razdoblje ?? ''}</td><td>{s.tem ?? ''}</td>
                                                        <td>{s.dokument ?? ''}</td><td title={s.partner_sifra ?? ''}>{s.partner ?? ''}</td>
                                                        <td>{s.opis ?? ''}</td><td style={num}>{m(s.iznos)}</td>
                                                    </tr>
                                                ))}
                                                {konteRez.neuparene.length === 0 ? <tr><td colSpan={8} className="muted">{t('Nema neuparenih stavki na ovom kontu.', 'No unmatched items on this account.')}</td></tr> : null}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 10, maxWidth: 520 }}>
                                        <h4 style={{ margin: '0 0 8px' }}>{t('Napravi nacrt zatvaranja', 'Create closing draft')}</h4>
                                        {nacrtGotov ? (
                                            <p>
                                                {t('Nacrt', 'Draft')} #{nacrtGotov} {t('napravljen.', 'created.')}{' '}
                                                {onOpenNacrti ? <button type="button" className="secondary compact" onClick={onOpenNacrti}>{t('Otvori nacrte', 'Open drafts')}</button> : null}
                                            </p>
                                        ) : (
                                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                <label>{t('Protukonto', 'Contra account')}<br />
                                                    <input value={protukonto} onChange={(e) => setProtukonto(e.target.value)} style={{ width: 120 }} />
                                                </label>
                                                <label>{t('Datum', 'Date')}<br />
                                                    <input type="date" value={nacrtDatum} onChange={(e) => setNacrtDatum(e.target.value)} />
                                                </label>
                                                <label>{t('Opis', 'Description')}<br />
                                                    <input value={nacrtOpis} onChange={(e) => setNacrtOpis(e.target.value)} style={{ width: 220 }} />
                                                </label>
                                                <button type="button" className="compact" onClick={napraviNacrt} disabled={nacrtBusy || !protukonto.trim() || odabrane.size === 0}>
                                                    {nacrtBusy ? t('Spremam…', 'Saving…') : `${t('Napravi nacrt', 'Create draft')} (${odabrane.size})`}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
